import { useGlue } from '@glue42/react-hooks';
import { useServices } from 'app/hooks';
import { registerInteropMethods } from 'app/glue/interop/register';
import { isContextWithToken } from 'app/glue/types';
import {
    buildLogger,
    isQueryParameterPresent,
} from '@instinet-analytics/core-lib';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { GlueBloombergBridge } from 'app/glue/bloomberg/GlueBloombergBridge';
import { BloombergExternalMarketDataStrategy } from 'app/glue/bloomberg/BloombergExternalMarketDataStrategy';
import { useIndigoMarketDataEnabled } from 'marketData/hooks';
import { Glue42Core } from '@glue42/core';
import { Glue42InteropExternalMarketDataStrategy } from 'app/glue/Glue42InteropExternalMarketDataStrategy';

export function useIndigoGlue() {
    const {
        embeddedApiService,
        loggingService,
        marketDataService,
        actionsIntervalDispatcherFactory,
        symbolsService,
    } = useServices();

    const logger = useMemo(
        () => buildLogger('useIndigoGlue', loggingService),
        [loggingService]
    );

    const indigoMarketDataEnabled = useIndigoMarketDataEnabled();

    const interop = useRef<Glue42Core.Interop.API | undefined>();
    const bbg = useRef<GlueBloombergBridge | undefined>();

    const registerInteropStrategy = useCallback(() => {
        const useInteropStrategy = isQueryParameterPresent('mktDataInterop', [
            '1',
            'true',
        ]);

        if (!interop.current) {
            return;
        }

        if (useInteropStrategy) {
            const strategy = new Glue42InteropExternalMarketDataStrategy({
                glueInteropApi: interop.current,
                loggingService,
                symbolsService,
            });
            marketDataService.setStrategy(strategy);
            return true;
        }
    }, [loggingService, symbolsService, marketDataService]);

    const bootstrapBloomberg = useCallback(() => {
        if (registerInteropStrategy()) {
            return;
        }

        if (bbg.current) {
            return;
        }
        if (!interop.current) {
            return;
        }

        logger.info('Indigo market data is disabled: bootstrapping Bloomberg');
        const bridge = new GlueBloombergBridge({
            embeddedApiService,
            glueInteropApi: interop.current,
            loggingService,
            actionsIntervalDispatcherFactory,
        });
        const strategy = new BloombergExternalMarketDataStrategy(bridge);
        marketDataService.setStrategy(strategy);

        bbg.current = bridge;
    }, [
        marketDataService,
        embeddedApiService,
        logger,
        loggingService,
        actionsIntervalDispatcherFactory,
        registerInteropStrategy,
    ]);

    useGlue(
        (glue) => {
            (async () => {
                interop.current = glue.interop;
                await registerInteropMethods(glue, embeddedApiService, logger);

                const me = glue.windows.my();
                const context = await me.getContext();

                if (isContextWithToken(context)) {
                    logger.debug('Authenticating with Glue context', context);
                    await embeddedApiService.receiveMessage(
                        'AUTHENTICATE',
                        context
                    );
                } else if (context) {
                    logger.warn('Unrecognised Glue context', context);
                }

                if (!indigoMarketDataEnabled) {
                    bootstrapBloomberg();
                }
            })();
        },
        [embeddedApiService, loggingService, bootstrapBloomberg]
    );

    useEffect(() => {
        if (!indigoMarketDataEnabled) {
            bootstrapBloomberg();
        }
    }, [indigoMarketDataEnabled, logger, bootstrapBloomberg]);

    useEffect(() => {
        return () => {
            if (bbg.current) {
                bbg.current.dispose();
            }
        };
    }, []);
}
